import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { useSellPayoutList } from './hooks'
import { SellPayout } from './types'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Badge, InputRef, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { FilterValue } from 'antd/es/table/interface'
import { useStore } from 'effector-react'
import { $userRole } from '../../../../features/user'
import dayjs from 'dayjs'
import {
  getColumnSearchDateProps,
  getColumnSearchProps,
} from '../../tableFunctions'
import { path, role } from '../../../../libs'
import { convertSellOfferStatus, statusFilter } from './consts'
import { Order } from '../../orders/list/types'
import {
  $sellPayoutsWithoutPagination,
  sellPayoutListWithoutPaginationMounted,
} from './model'

export const SellPayoutList = () => {
  const {
    sellPayouts,
    totalCountOfSellPayouts,
    loading,
    onChangeSellPayoutList,
  } = useSellPayoutList()

  const sellPayoutsWithoutPagination = useStore($sellPayoutsWithoutPagination)

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [searchedColumn, setSearchedColumn] = useState('')
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const userRole = useStore($userRole)

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    sellPayoutListWithoutPaginationMounted()
  }, [])

  useEffect(() => {
    onChangeSellPayoutList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const getCsvCode = () => {
    if (sellPayoutsWithoutPagination.length == 0) {
      return 'data:text/csv;charset=utf-8,'
    }
    const csvContent = sellPayoutsWithoutPagination.map((item) => {
      return {
        ID: item.id,
        'Hot buy order ID': item.sellOfferBuyId,
        'Sell offer ID': item.sellOfferId,
        'Seller User ID': item.clientId,
        'Buyer User ID': item.buyerClientId,
        Date: new Date(item.addedAt).toLocaleString().replace(',', ' '),
        'Property Name': item.propertyName,
        'Numbers of tokens': +item.tokenAmount,
        'Hot buy order amount $': `${+item.totalPriceTaker} usd`,
        'Total sell amount $': `${+item.totalPriceMaker} usd`,
        'NOVA $ commission': `${+item.totalPriceFee} usd`,
        'NOVA % commission': `${+item.tokenPriceFeePercent} usd`,
      }
    })
    const header = Object.keys(csvContent[0])?.join(',')
    const data = csvContent.map((item) => Object.values(item).join(','))
    return (
      'data:text/csv;charset=utf-8, ' +
      encodeURIComponent([header, ...data].join('\n'))
    )
  }

  const columns: ColumnsType<SellPayout> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps<SellPayout>(
        'id',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) => parseInt(a.id) - parseInt(b.id),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Hot buy order ID',
      dataIndex: 'sellOfferBuyId',
      ...getColumnSearchProps<SellPayout>(
        'sellOfferBuyId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) => parseInt(a.sellOfferBuyId) - parseInt(b.sellOfferBuyId),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Sell offer ID',
      dataIndex: 'sellOfferId',
      ...getColumnSearchProps<SellPayout>(
        'sellOfferId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) => parseInt(a.sellOfferId) - parseInt(b.sellOfferId),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Seller User ID',
      ...getColumnSearchProps<SellPayout>(
        'clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: SellPayout) => (
        <div>
          {item.user?.isTest ? (
            <Badge count={'Test'} offset={[10, -10]}>
              <a
                href={path.clients.details(String(item.clientId))}
                target="_blank"
              >
                {item.clientId}
              </a>
            </Badge>
          ) : (
            <a
              href={path.clients.details(String(item.clientId))}
              target="_blank"
            >
              {item.clientId}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Buyer User ID',
      ...getColumnSearchProps<SellPayout>(
        'buyerClientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: SellPayout) => (
        <div>
          {item.buyerUser?.isTest ? (
            <Badge count={'Test'} offset={[10, -10]}>
              <a
                href={path.clients.details(String(item.buyerClientId))}
                target="_blank"
              >
                {item.buyerClientId}
              </a>
            </Badge>
          ) : (
            <a
              href={path.clients.details(String(item.buyerClientId))}
              target="_blank"
            >
              {item.buyerClientId}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'addedAt',
      ...getColumnSearchDateProps<SellPayout>(
        'addedAt',
        setSearchText,
        searchText,
        setIsCalendarOpen,
        isCalendarOpen,
        searchInput,
      ),
      render: (item) => <div>{new Date(item).toLocaleString()}</div>,
      sorter: true,
      width: '150px',
    },
    {
      title: 'Property Name',
      dataIndex: 'propertyName',
    },
    {
      title: 'Numbers of tokens',
      dataIndex: 'tokenAmount',
      render: (item) => <div>{+item}</div>,
    },
    {
      title: 'Hot buy order amount $',
      dataIndex: 'totalPriceTaker',
      render: (item) => <div>{+item + ' USD'}</div>,
    },
    {
      title: 'Total sell amount $',
      dataIndex: 'totalPriceMaker',
      render: (item) => <div>{+item + ' USD'}</div>,
    },
    {
      title: 'NOVA $ commission',
      dataIndex: 'totalPriceFee',
      render: (item) => <div>{+item + ' USD'}</div>,
    },
    {
      title: 'NOVA % commission',
      dataIndex: 'tokenPriceFeePercent',
      render: (item) => <div>{+item}</div>,
    },
  ]

  const isDisplayDownloadButton =
    userRole?.filter((roleItem) =>
      [
        role.root(),
        role.head.cfo(),
        role.finances.financeManager(),
        role.nova(),
      ].includes(roleItem),
    ).length !== 0

  return (
    <CCardBody>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          {isDisplayDownloadButton && (
            <CButton
              color="primary"
              href={getCsvCode()}
              download={
                'orders ' +
                dayjs(Date.now()).format('YYYY-MM-DD HH-mm') +
                '.csv'
              }
              target="_blank"
            >
              Download current items (.csv)
            </CButton>
          )}
        </div>
      </div>
      <Table
        dataSource={sellPayouts}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {},
          }
        }}
        rowKey="id"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfSellPayouts,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters: { ...filtersState, ...filters },
          })
        }
      />
    </CCardBody>
  )
}
